
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { RecruitmentStatus, IntegrityFilter, ScoreFilter, FilterType } from "@/types/test-response";

interface RecruitmentFiltersProps {
  activeFilters: FilterType[];
  onFilterChange: (filters: FilterType[]) => void;
}

export function RecruitmentFilters({ activeFilters, onFilterChange }: RecruitmentFiltersProps) {
  const toggleFilter = (filter: FilterType) => {
    if (activeFilters.includes(filter)) {
      onFilterChange(activeFilters.filter(f => f !== filter));
    } else {
      onFilterChange([...activeFilters, filter]);
    }
  };

  const clearFilters = () => {
    onFilterChange([]);
  };

  const renderFilterBadge = (filter: FilterType, label: string) => (
    <Badge 
      key={filter}
      variant={activeFilters.includes(filter) ? 'default' : 'outline'}
      className="cursor-pointer hover:bg-secondary/80"
      onClick={() => toggleFilter(filter)}
    >
      {activeFilters.includes(filter) && (
        <Check className="h-3 w-3 mr-1" />
      )}
      {label}
    </Badge>
  );

  return (
    <div className="space-y-4">
      {/* Recruitment Status Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium text-muted-foreground min-w-16">Status:</span>
        <div className="flex flex-wrap gap-2">
          {renderFilterBadge('shortlisted', 'Shortlisted')}
          {renderFilterBadge('hired', 'Hired')}
          {renderFilterBadge('pending', 'Pending')}
          {renderFilterBadge('rejected', 'Rejected')}
        </div>
      </div>

      {/* Integrity Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium text-muted-foreground min-w-16">Integrity:</span>
        <div className="flex flex-wrap gap-2">
          {renderFilterBadge('integrity_50', 'Above 50%')}
          {renderFilterBadge('integrity_70', 'Above 70%')}
          {renderFilterBadge('integrity_90', 'Above 90%')}
        </div>
      </div>

      {/* Score Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium text-muted-foreground min-w-16">Score:</span>
        <div className="flex flex-wrap gap-2">
          {renderFilterBadge('score_50', 'Above 50%')}
          {renderFilterBadge('score_70', 'Above 70%')}
          {renderFilterBadge('score_90', 'Above 90%')}
        </div>
      </div>
    </div>
  );
}
